/**
 * KAN-788: фильтр по атрибуту лида: селектор "Атрибут" (Все + атрибуты) и
 * селектор "Значение" (Любое заполненное + варианты; у текстового атрибута
 * вариантов нет, селектор значения скрыт). Общий для доски CRM и списка
 * клиентов. Без атрибутов не рисуется. Смена атрибута сбрасывает значение.
 * KAN-906: compact - без подписей над полями, в поле "Атрибут: Все" (таблица CRM).
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { COMPACT_TRIGGER_CLASS, FILTER_TRIGGER_CLASS, FilterField } from '@/components/clients/FilterField';
import {
  attributeFilterOptions,
  type CrmAttributeFilter as AttributeFilterValue,
  type FilterableAttribute,
} from '@/lib/crmAttributeFilter';

/** Пункт "Все": фильтра нет. */
const ALL_ATTRIBUTES = '__all__';
/** Пункт "Любое заполненное": значение не задано. */
const ANY_VALUE = '__any__';

interface CrmAttributeFilterProps {
  attributes: readonly FilterableAttribute[] | undefined;
  filter: AttributeFilterValue | null;
  onChange: (filter: AttributeFilterValue | null) => void;
  /** KAN-906: компактная строка: высота h-8, подпись внутри поля. */
  compact?: boolean;
}

export function CrmAttributeFilter({
  attributes,
  filter,
  onChange,
  compact = false,
}: CrmAttributeFilterProps): JSX.Element | null {
  if (!attributes || attributes.length === 0) return null;
  const selected = filter ? attributes.find((a) => a.id === filter.attr) : undefined;
  const options = selected ? attributeFilterOptions(selected) : [];
  const triggerClass = compact ? COMPACT_TRIGGER_CLASS : FILTER_TRIGGER_CLASS;

  return (
    <>
      <FilterField label="Атрибут" hideLabel={compact}>
        <Select
          value={selected ? selected.id : ALL_ATTRIBUTES}
          onValueChange={(v) => onChange(v === ALL_ATTRIBUTES ? null : { attr: v, value: null })}
        >
          <SelectTrigger data-testid="attribute-filter" aria-label="Атрибут" className={triggerClass}>
            {compact && !selected ? (
              <SelectValue className="whitespace-nowrap">Атрибут: Все</SelectValue>
            ) : (
              <SelectValue placeholder="Атрибут" className="whitespace-nowrap" />
            )}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ATTRIBUTES}>Все</SelectItem>
            {attributes.map((attribute) => (
              <SelectItem key={attribute.id} value={attribute.id}>
                {attribute.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {selected && options.length > 0 && (
        <FilterField label="Значение" hideLabel={compact}>
          <Select
            value={filter?.value ?? ANY_VALUE}
            onValueChange={(v) => onChange({ attr: selected.id, value: v === ANY_VALUE ? null : v })}
          >
            <SelectTrigger
              data-testid="attribute-value-filter"
              aria-label="Значение атрибута"
              className={triggerClass}
            >
              <SelectValue placeholder="Значение" className="whitespace-nowrap" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_VALUE}>Любое заполненное</SelectItem>
              {options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
      )}
    </>
  );
}
